import React, { useContext } from 'react';
import { ApolloProvider } from 'react-apollo';

import { Auth } from 'aws-amplify';
// eslint-disable-next-line import/no-extraneous-dependencies
import { ApolloLink } from 'apollo-link';
import { withClientState } from 'apollo-link-state';
import AWSAppSyncClient, {
  createAppSyncLink,
  createLinkWithCache
} from 'aws-appsync';

import AWSConfig from 'config/aws/aws-exports';
import componentUtil from 'utils/component';
import Resolvers from 'graphql/client/resolvers';
import { SnackbarContext } from 'components/theme/snackbar';

import getErrorLink from './error-link';

function getStateLink() {
  return createLinkWithCache((cache) => {
    const linkWithClientState = withClientState({
      cache,
      resolvers: Resolvers
    });

    cache.writeData({
      data: {
        appContext: [],
        envContext: []
      }
    });

    return linkWithClientState;
  });
}

function getAppSyncLink() {
  const AWSAppSyncConfig = AWSConfig.AppSync;
  return createAppSyncLink({
    url: AWSAppSyncConfig.aws_appsync_graphqlEndpoint,
    region: AWSAppSyncConfig.aws_appsync_region,
    auth: {
      type: AWSAppSyncConfig.aws_appsync_authenticationType,
      jwtToken: async () =>
        (await Auth.currentSession()).getIdToken().getJwtToken(),
      apiKey: AWSAppSyncConfig.aws_appsync_apiKey
    }
  });
}

export default function withApolloProvider(Component) {
  const WithApolloProvider = (props) => {
    const { showSnackbar } = useContext(SnackbarContext);

    const link = ApolloLink.from([
      getErrorLink(showSnackbar),
      getStateLink(),
      getAppSyncLink()
    ]);

    const awsAppSyncClient = new AWSAppSyncClient(
      {
        disableOffline: true,
        cacheOptions: {
          dataIdFromObject: (obj) => {
            return `${obj.__typename}:${obj.uId}`;
          }
        }
      },
      {
        link,
        defaultOptions: {
          watchQuery: {
            fetchPolicy: 'network-only',
            errorPolicy: 'none'
          },
          query: {
            fetchPolicy: 'network-only',
            errorPolicy: 'none'
          },
          mutate: {
            errorPolicy: 'none'
          }
        }
      }
    );

    return (
      <>
        <ApolloProvider client={awsAppSyncClient}>
          <Component {...props} />
        </ApolloProvider>
      </>
    );
  };
  WithApolloProvider.displayName = componentUtil.getDisplayName(
    Component,
    'WithApolloProvider'
  );
  return WithApolloProvider;
}
